
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PendingPrompt {
  id: string;
  title: string;
  description: string;
  content: string;
  category: {
    id: string;
    name: string;
    slug: string;
  };
  user_id: string;
  author: {
    name: string;
    email: string;
  };
  submittedAt: string;
}

export const usePromptManagement = () => {
  const [pendingPrompts, setPendingPrompts] = useState<PendingPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { toast } = useToast();

  const fetchPendingPrompts = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select(`
          id, 
          title, 
          description, 
          content,
          created_at,
          user_id,
          category_id,
          categories(id, name, slug)
        `)
        .eq('status', 'pending');

      if (error) throw error;
      
      console.log('Pending prompts data:', data);

      const promptsWithAuthor = await Promise.all(
        data.map(async (prompt) => {
          const { data: userData, error: userError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .eq('id', prompt.user_id)
            .single();

          if (userError) console.error('Error fetching user data:', userError);

          let userEmail = "Email not available";

          return {
            id: prompt.id,
            title: prompt.title,
            description: prompt.description,
            content: prompt.content,
            category: {
              id: prompt.categories?.id || '',
              name: prompt.categories?.name || 'Uncategorized',
              slug: prompt.categories?.slug || ''
            },
            user_id: prompt.user_id,
            author: {
              name: userData 
                ? [userData.first_name, userData.last_name].filter(Boolean).join(' ') 
                : 'Anonymous',
              email: userEmail
            },
            submittedAt: prompt.created_at
          };
        })
      );

      setPendingPrompts(promptsWithAuthor);
    } catch (error: any) {
      console.error('Error fetching pending prompts:', error);
      toast({
        title: "Error fetching prompts",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingPrompts();
  }, [refreshTrigger]);

  const handleApprove = async (id: string) => {
    try {
      const { error } = await supabase
        .from('prompts')
        .update({ status: 'approved' })
        .eq('id', id);

      if (error) throw error;

      setPendingPrompts(prev => prev.filter(prompt => prompt.id !== id));
      toast({
        title: "Prompt approved",
        description: "The prompt has been approved and published.",
      });
    } catch (error: any) {
      console.error('Error approving prompt:', error);
      toast({
        title: "Error approving prompt",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleReject = async (id: string) => {
    try {
      const { error } = await supabase
        .from('prompts')
        .update({ status: 'rejected' })
        .eq('id', id);

      if (error) throw error;

      setPendingPrompts(prev => prev.filter(prompt => prompt.id !== id));
      toast({
        title: "Prompt rejected",
        description: "The prompt has been rejected.",
      });
    } catch (error: any) {
      console.error('Error rejecting prompt:', error);
      toast({
        title: "Error rejecting prompt",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('prompts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setPendingPrompts(prev => prev.filter(prompt => prompt.id !== id));
      setRefreshTrigger(prev => prev + 1);
      toast({
        title: "Prompt deleted",
        description: "The prompt has been permanently deleted.",
      });
    } catch (error: any) {
      console.error('Error deleting prompt:', error);
      toast({
        title: "Error deleting prompt",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return {
    pendingPrompts,
    isLoading,
    fetchPendingPrompts,
    handleApprove,
    handleReject,
    handleDelete,
    refreshTrigger,
    setRefreshTrigger
  };
};
